import React from 'react';
import { ThemeProvider } from 'styled-components';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import GlobalStyles from './global-styles';

const theme = {
  colors: {
    black: '#000000',
    black100: '#222028',
    black90: '#2D2A38',
    black80: '#413E4D',
    black70: '#55545C',
    black60: '#686573',
    black50: '#9A98A0',
    cream100: '#EEE6DB',
    cream200: '#D2CFCA',
    green100: '#78CB0E',
    orange50: '#A65D28',
    orange100: '#DA7E3C',
    orange200: '#FF7C1E',
    red100: '#F5513A',
    blue100: '#559AEB',
    yellow100: '#F4F431',
    white: '#FFFFFF',
  },
  fonts: {
    families: {
      text: 'Roboto, "Helvetica Neue", san-serif',
      display: 'Poppins, "Helvetica Neue", san-serif',
      numerals: 'Numerals, source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
      code: 'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
    },
    weights: {
      light: 300,
      regular: 400,
      medium: 500,
      semiBold: 600,
      bold: 700,
      extraBold: 800,
      black: 900,
    },
    sizes: {
      base: '16px',
      xxxxxlg: '4rem', //  64px
      xxxxlg: '3rem', //   48px
      xxxlg: '2rem', //    32px
      xxlg: '1.5rem', //   24px
      xlg: '1.25rem', //   20px
      lg: '1rem', //       16px
      reg: '0.875rem', //  14px
      sm: '0.75rem', //    12px
      xsm: '0.6875rem', // 11px
      xxsm: '0.625rem', // 10px
    },
  },
};

export let muiTheme = createTheme({
  spacing: 16,
  palette: {
    primary: {
      main: '#3d4245',
      dark: '#080808',
      light: '#080808',
      error: theme.colors.red100,
      success: theme.colors.green100,
    },
    secondary: {
      main: theme.colors.orange100,
    },
  },
  typography: {
    fontFamily: theme.fonts.families.text,
    h1: {
      fontFamily: theme.fonts.families.display,
      margin: 0,
    },
    h2: {
      fontFamily: theme.fonts.families.display,
      margin: 0,
    },
    h3: {
      fontFamily: theme.fonts.families.display,
      margin: 0,
    },
    h4: {
      fontFamily: theme.fonts.families.display,
      margin: 0,
    },
    h5: {
      fontFamily: theme.fonts.families.display,
      margin: 0,
    },
    h6: {
      fontFamily: theme.fonts.families.display,
      margin: 0,
    },
  },
  props: {
    MuiTab: {
      disableRipple: true,
    },
  },
  mixins: {
    toolbar: {
      minHeight: 48,
    },
  },
});

muiTheme = {
  ...muiTheme,
  overrides: {
    MuiCssBaseline: {
      '@global': {
        html: {},
        textarea: {
          fontFamily: theme.fonts.families.text,
        },
        code: {
          fontFamily: theme.fonts.families.code,
        },
      },
    },
    MuiDrawer: {
      paper: {
        backgroundColor: muiTheme.palette.primary.main,
      },
    },
    MuiButton: {
      label: {
        textTransform: 'none',
      },
    },
    MuiTableContainer: {
      root: {
        paddingBottom: '1rem',
      },
    },
    MuiTabs: {
      root: {
        marginLeft: muiTheme.spacing(0.5),
      },
      indicator: {
        height: 3,
        borderTopLeftRadius: 3,
        borderTopRightRadius: 3,
        backgroundColor: muiTheme.palette.common.white,
      },
    },
    MuiTab: {
      root: {
        textTransform: 'none',
        margin: '0 16px',
        minWidth: 0,
        padding: 0,
        [muiTheme.breakpoints.up('md')]: {
          padding: 0,
          minWidth: 0,
        },
      },
    },
    MuiIconButton: {
      root: {
        padding: muiTheme.spacing(0.5),
      },
    },
    MuiTooltip: {
      tooltip: {
        borderRadius: 4,
      },
    },
    MuiToolbar: {
      gutters: {
        [muiTheme.breakpoints.up('xs')]: {
          padding: muiTheme.spacing(0, 2),
        },
      },
    },
    MuiDivider: {
      root: {
        backgroundColor: '#404854',
      },
    },
    MuiListItemText: {
      primary: {
        fontWeight: muiTheme.typography.fontWeightMedium,
      },
    },
    MuiListItemIcon: {
      root: {
        color: 'inherit',
        marginRight: 0,
        '& svg': {
          fontSize: 20,
        },
      },
    },
    MuiAvatar: {
      root: {
        width: 32,
        height: 32,
      },
    },
  },
};

const Theme = ({ children }) => (
  <MuiThemeProvider theme={muiTheme}>
    <ThemeProvider theme={theme}>
      {children}
      <GlobalStyles />
    </ThemeProvider>
  </MuiThemeProvider>
);

export default Theme;
